/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Backdrop from '@mui/material/Backdrop';
import Modal from '@mui/material/Modal';
import Fade from '@mui/material/Fade';
import CashDenominationCounter from '../components/modals/CashDenominationCounter';
import CustomBtn from 'components/fragments/CustomBtn';
import PinVerificationModal from 'components/modals/PinVerificationModal';
import TransactionSuccessModal from 'components/modals/TransactionSuccessModal';
import CloseBalanceConfirmationModal from 'components/modals/CloseBalanceConfirmationModal';
import CustomInput from 'components/fragments/CustomInput';
import { numberWithTwoDecimals } from 'utils/numberFormat';
import { getCurrentDateTime } from 'utils/getCurrentDateTime';
import { posHandler, billHandler } from '../redux/handlers';
import { useParams } from 'react-router-dom';
import ModalPOSCashTransactionPrint from '../components/modals/printing-forms/ModalPOSCashTransactionPrint';
import moment from 'moment';

const DRAWER_OPERATION_TYPES = {
  DEPOSIT: 0,
  WITHDRAW: 1,
  CLOSE_BALANCE: 2
};

const INITIAL_CASH_BREAKDOWN = {
  cash_1000: 0,
  cash_500: 0,
  cash_100: 0,
  cash_50: 0,
  cash_20: 0,
  cash_10: 0,
  cash_5: 0,
  cash_1: 0
};

const INITIAL_CASHIER_INFO = {
  operationType: '',
  operationDescription: '',
  cashierName: ''
};

const DEFAULT_CASH_DATA = {
  initial_cash: 0,
  total_cash_in: 0,
  total_cash_out: 0,
  total_sales: 0,
  fee_revenue: 0,
  total_amount: 0,
  system_balance: {
    cash: 0,
    bank_transfer: 0,
    credit_card: 0,
    payment_gateway: 0,
    accounts_receivable: 0,
    coupon: 0,
    other: 0
  }
};

export default function CashBreakdownCounterModal({
  modalOpenObj,
  currentMenu,
  cashFloatData = [],
  cashFloatCount = 0,
  totalCashFloat = 0,
  cashRemovalData = [],
  cashRemovalCount = 0,
  totalCashRemoval = 0
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { businessSlug } = useParams();
  const currentBranchId = localStorage.getItem('branchId');
  const user = useSelector(state => state.pos.user);
  const { pos } = useSelector(state => state.pos);
  const [cashAmount, setCashAmount] = useState('0.00');
  const [isSubmitButtonActive, setIsSubmitButtonActive] = useState(false);
  const [isPinModalOpen, setIsPinModalOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isCloseBalanceConfirmModalOpen, setIsCloseBalanceConfirmModalOpen] = useState(false);
  const [isTransactionPrintPopupOpen, setIsTransactionPrintPopupOpen] = useState(false);

  const [shouldResetMoneyController, setShouldResetMoneyController] = useState(false);
  const [isBalanceCloseConfirmed, setIsBalanceCloseConfirmed] = useState(false);

  const [initialCashData, setInitialCashData] = useState(null);
  const [summaryData, setSummaryData] = useState(DEFAULT_CASH_DATA);
  const [isLoading, setIsLoading] = useState(false);
  const [posData, setPosData] = useState(null);
  const [businessInfo, setBusinessInfo] = useState({
    businessName: '',
    branchName: '',
    vatCode: ''
  });

  const [cashBreakdown, setCashBreakdown] = useState(INITIAL_CASH_BREAKDOWN);
  const [backupCashBreakdown, setBackupCashBreakdown] = useState(INITIAL_CASH_BREAKDOWN);
  const [cashierOperationInfo, setCashierOperationInfo] = useState(INITIAL_CASHIER_INFO);
  const [currentOperation, setCurrentOperation] = useState({
    headerText: '',
    successModalHeader: '',
    buttonText: '',
    type: ''
  });

  const [paymentMethodTotals, setPaymentMethodTotals] = useState({
    transferAmount: 0,
    otherPaymentAmount: 0,
    cashAmount: 0,
    differenceAmount: 0
  });

  const [hasPendingBills, setHasPendingBills] = useState(false);
  const [pendingBillsErrorMessage, setPendingBillsErrorMessage] = useState('');

  useEffect(() => {
    setIsSubmitButtonActive(parseFloat(cashAmount) > 0);
  }, [cashAmount]);

  useEffect(() => {
    setIsBalanceCloseConfirmed(true);
  }, []);

  useEffect(() => {
    if (modalOpenObj.modalOpen) {
      setCashBreakdown(INITIAL_CASH_BREAKDOWN);
      setShouldResetMoneyController(true);
    }
  }, [modalOpenObj.modalOpen]);

  useEffect(() => {
    if (modalOpenObj.modalOpen && currentMenu === DRAWER_OPERATION_TYPES.CLOSE_BALANCE) {
      checkPendingBills();
    }
  }, [modalOpenObj.modalOpen, currentMenu]);

  const checkPendingBills = async () => {
    try {
      setIsLoading(true);
      const branchPOSSellingId = localStorage.getItem('branchPOSSelling');

      if (branchPOSSellingId) {
        const response = await dispatch(billHandler.getBill(businessSlug, branchPOSSellingId, undefined, 'PENDING'));

        if (response && response.length > 0) {
          setHasPendingBills(true);

          setPendingBillsErrorMessage(`มีรายการบิลขายที่ยังไม่ได้ชำระรายการ กรุณาปิดทั้งหมดก่อน`);
          return true;
        } else {
          setHasPendingBills(false);
          setPendingBillsErrorMessage('');
          return false;
        }
      }
    } catch (error) {
      setHasPendingBills(true);
      setPendingBillsErrorMessage('เกิดข้อผิดพลาดในการตรวจสอบบิลค้างชำระ');
      console.error('Error checking pending bills:', error);
      return true;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const fetchPosData = async () => {
      if (modalOpenObj.modalOpen) {
        try {
          const branchId = localStorage.getItem('branchId');
          if (branchId) {
            const response = await dispatch(posHandler.getBranchPosById(businessSlug, branchId));
            if (response) {
              setPosData(response);
            }
          }
        } catch (error) {
          console.error('Error fetching POS data:', error);
        }
      }
    };

    const fetchBusinessInfo = async () => {
      try {
        const branchId = localStorage.getItem('branchId');
        if (branchId) {
          const response = await dispatch(posHandler.getBranchInfo(businessSlug, branchId));
          if (response) {
            let businessName = '';
            let branchName = '';
            let vatCode = '';
            let taxNumber = '';
            let vatCalculator = '';

            if (response?.employee_info?.branch) {
              businessName = response.employee_info.branch.business?.company_name || '';
              branchName = response.employee_info.branch.branch_name || '';
            } else if (response?.branch_info?.business) {
              businessName = response.branch_info.business?.company_name || '';
              branchName = response.branch_info.branch_name || '';
              vatCode = response.branch_info.vat_code || '';
              taxNumber = response.branch_info.business?.tax_number || '';
              vatCalculator = response.branch_info.business?.settingVatCalculation?.code || '';
            }

            setBusinessInfo({
              businessName,
              branchName,
              vatCode,
              taxNumber,
              vatCalculator
            });
          }
        }
      } catch (error) {
        console.error('Error fetching business info:', error);
      }
    };

    fetchPosData();
    fetchBusinessInfo();
    dispatch(posHandler.getBranchPosById(businessSlug, currentBranchId));
  }, [modalOpenObj.modalOpen, dispatch, businessSlug, currentBranchId]);

  const fetchInitialCashData = useCallback(async () => {
    try {
      setIsLoading(true);
      const branchPOSSellingId = localStorage.getItem('branchPOSSelling');
      if (branchPOSSellingId) {
        const response = await dispatch(posHandler.getInitialCash(businessSlug, branchPOSSellingId));
        if (response) {
          setInitialCashData(response);
        }
      }
    } catch (error) {
      console.error('Error fetching initial cash data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [businessSlug, dispatch]);

  const fetchSummaryData = useCallback(async () => {
    try {
      setIsLoading(true);
      const branchPOSSellingId = localStorage.getItem('branchPOSSelling');
      if (branchPOSSellingId) {
        const response = await dispatch(posHandler.getSummaryDataCancel(businessSlug, branchPOSSellingId));
        if (response) {
          const transformedData = {
            initial_cash: response.initial_cash || 0,
            total_cash_in: response.cash_float || 0,
            total_cash_out: response.cash_removal || 0,
            total_sales: response.total_final_amount || 0,
            fee_revenue: response.fee_income || 0,
            sale_refund: response.sale_refund || 0,
            total_amount: (response.total_final_amount || 0) + (response.fee_income || 0),
            system_balance: {
              cash: response.system_balance?.find(pm => pm.payment_method_code === 'CASH')?.amount || 0,
              bank_transfer:
                response.system_balance?.find(pm => pm.payment_method_code === 'BANK_TRANSFER')?.amount || 0,
              credit_card: response.system_balance?.find(pm => pm.payment_method_code === 'CREDIT_CARD')?.amount || 0,
              payment_gateway: response.system_balance?.find(pm => pm.payment_method_code === 'QR')?.amount || 0,
              accounts_receivable:
                response.system_balance?.find(pm => pm.payment_method_code === 'ACCOUNT_RECEIVABLE')?.amount || 0,
              coupon: response.system_balance?.find(pm => pm.payment_method_code === 'COUPON')?.amount || 0,
              promotion: response.system_balance?.find(pm => pm.payment_method_code === 'PPROMOTION')?.amount || 0,
              point: response.system_balance?.find(pm => pm.payment_method_code === 'POINT')?.amount || 0,
              delivery: response.system_balance?.find(pm => pm.payment_method_code === 'DELIVERY')?.amount || 0,
              other:
                (response.system_balance?.find(pm => pm.payment_method_code === 'ช่องทางอื่นๆ')?.amount || 0) +
                (response.system_balance?.find(pm => pm.payment_method_code === 'ตั้งค่าอื่นๆ')?.amount || 0)
            }
          };
          setSummaryData(transformedData);
        }
      }
    } catch (error) {
      console.error('Error fetching summary data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [businessSlug, dispatch]);

  useEffect(() => {
    if (modalOpenObj.modalOpen) {
      fetchInitialCashData();
      if (currentMenu === DRAWER_OPERATION_TYPES.CLOSE_BALANCE) {
        fetchSummaryData();
      }
    }
  }, [modalOpenObj.modalOpen, fetchInitialCashData, fetchSummaryData, currentMenu]);

  useEffect(() => {
    const getPosName = () => {
      if (posData?.data?.name) {
        return posData.data.name;
      }
      return '';
    };

    const getUserFullName = () => {
      if (!user) return '...';
      const firstName = user.first_name || '';
      const lastName = user.last_name || '';
      return `${firstName} ${lastName}`.trim();
    };

    const updateOperationContext = () => {
      const posName = getPosName();

      switch (currentMenu) {
        case DRAWER_OPERATION_TYPES.DEPOSIT:
          setCurrentOperation({
            headerText: 'นำเงินเข้าครั้งที่',
            successModalHeader: 'นำเงินเข้าสำเร็จ',
            buttonText: 'นำเงินเข้า',
            type: 'deposit'
          });
          setCashierOperationInfo({
            cashierName: posName,
            staffName: getUserFullName(),
            startTime: getCurrentDateTime(),
            operationType: 'เพิ่ม',
            operationDescription: 'เงินสดนำเข้า'
          });
          break;

        case DRAWER_OPERATION_TYPES.WITHDRAW:
          setCurrentOperation({
            headerText: 'นำเงินออกครั้งที่',
            successModalHeader: 'นำเงินออกสำเร็จ',
            buttonText: 'นำเงินออก',
            type: 'withdraw'
          });
          setCashierOperationInfo({
            cashierName: posName,
            staffName: getUserFullName(),
            startTime: getCurrentDateTime(),
            operationType: 'นำออก',
            operationDescription: 'เงินสดนำออก'
          });
          break;

        case DRAWER_OPERATION_TYPES.CLOSE_BALANCE:
          setCurrentOperation({
            headerText: 'ปิดยอด',
            type: 'close'
          });
          setCashierOperationInfo({
            cashierName: posName,
            staffName: getUserFullName(),
            startTime: getCurrentDateTime(),

            operationDescription: 'เงินสดตั้งต้น',
            initialAmount: initialCashData ? initialCashData.cash_total : 0,
            totalDepositAmount: summaryData.total_cash_in,
            totalWithdrawalAmount: summaryData.total_cash_out,
            grandTotalAmount: summaryData.total_amount,
            feeIncomeAmount: summaryData.fee_revenue
          });
          break;

        default:
          break;
      }
    };

    setCashBreakdown(INITIAL_CASH_BREAKDOWN);

    if (currentMenu !== DRAWER_OPERATION_TYPES.CLOSE_BALANCE) {
      setShouldResetMoneyController(true);
      setCashierOperationInfo(INITIAL_CASHIER_INFO);
    }

    updateOperationContext();
  }, [currentMenu, initialCashData, user, posData, summaryData]);

  const updateCashBreakdown = breakdown => {
    setCashBreakdown(breakdown);
  };

  const processOperation = async () => {
    if (posData && posData[0]?.branchPosSelling?.length > 0) {
      const branchPosId = localStorage.getItem('branchPOSId');
      const posSellingId = posData[0].branchPosSelling[0].id;

      const requestData = {
        posSellingId: parseInt(posSellingId),
        branchPosId: parseInt(branchPosId),
        ...cashBreakdown
      };

      let apiResponse;
      setBackupCashBreakdown(cashBreakdown);
      switch (currentMenu) {
        case DRAWER_OPERATION_TYPES.DEPOSIT:
          apiResponse = await dispatch(posHandler.cashFloat(businessSlug, requestData));
          break;
        case DRAWER_OPERATION_TYPES.WITHDRAW:
          apiResponse = await dispatch(posHandler.cashRemoval(businessSlug, requestData));
          break;
        case DRAWER_OPERATION_TYPES.CLOSE_BALANCE:
          const closeCashLogData = {
            ...requestData,
            summary: {
              diff: calDiff(),
              initial_cash: summaryData.initial_cash,
              total_cash_in: summaryData.total_cash_in,
              total_cash_out: summaryData.total_cash_out,
              total_sales: summaryData.total_sales,
              fee_revenue: summaryData.fee_revenue,
              total_amount: summaryData.total_amount,
              system_balance: summaryData.system_balance
            }
          };
          apiResponse = await dispatch(posHandler.closeCashLog(businessSlug, closeCashLogData));
          break;
        default:
          break;
      }

      if (apiResponse) {
        if (currentMenu === DRAWER_OPERATION_TYPES.DEPOSIT || currentMenu === DRAWER_OPERATION_TYPES.WITHDRAW) {
          await fetchInitialCashData();

          setIsSuccessModalOpen(true);
        }

        return true;
      } else {
        console.error('API response failed');
        return false;
      }
    }
    return false;
  };

  const handlePinVerificationSuccess = async () => {
    try {
      setIsLoading(true);

      if (currentMenu === DRAWER_OPERATION_TYPES.DEPOSIT || currentMenu === DRAWER_OPERATION_TYPES.WITHDRAW) {
        await processOperation();
      } else if (currentMenu === DRAWER_OPERATION_TYPES.CLOSE_BALANCE) {
        setIsCloseBalanceConfirmModalOpen(true);
      }
    } catch (error) {
      console.error('Error processing transaction:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmCloseBalance = async () => {
    try {
      setIsLoading(true);
      const success = await processOperation();
      const startDate = new Date(cashierOperationInfo.startTime);
      const currentTime = new Date();

      const timeDiffMs = currentTime - startDate;

      const hours = Math.floor(timeDiffMs / (1000 * 60 * 60));
      const minutes = Math.floor((timeDiffMs % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((timeDiffMs % (1000 * 60)) / 1000);

      const timeDiff = [
        String(hours).padStart(2, '0'),
        String(minutes).padStart(2, '0'),
        String(seconds).padStart(2, '0')
      ].join(':');
      localStorage.removeItem('closePrintObj');
      let toLocal = {
        name: businessInfo.branchName,
        business: businessInfo.businessName,
        startTime: moment(startDate).format('DD/MM/YYYY HH:MM'),
        staff: cashierOperationInfo.staffName,
        posNO: posData?.[0].pos_id,
        endTime: moment().format('DD/MM/YYYY HH:MM'),
        duration: timeDiff,
        saleAmount: 0,
        returnProduct: 0,
        cancelProduct: 0,
        taxInvoice: 0,
        initialAmount: cashierOperationInfo.initialAmount,
        totalSale: cashAmount,
        saleRefund: 0,
        afterRefund: 0,
        itemDiscount: 0,
        subDiscount: 0,
        paintDiscount: 0,
        total: cashAmount,
        vat: 0,
        valueWithNoTax: 0,
        valueOfProduct: 0,
        tax: 0,
        net: cashAmount,
        cash: 0,
        transfer: 0,
        provider: 0,
        debtor: 0,
        coupon: 0,
        credit: {
          name: 'placeholder',
          payedAmount: cashAmount
        },
        cashCount: cashAmount,
        diff: calDiff()
      };
      toLocal = JSON.stringify(toLocal);
      localStorage.setItem('closePrintObj', toLocal);
      if (success) {
        navigate(`/${businessSlug}/close-balance-print`);
      }
    } catch (error) {
      console.error('Error processing close balance after confirmation:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = () => {
    modalOpenObj.setModalOpen(false);
    setIsPinModalOpen(true);
  };

  const handleCloseModal = () => {
    modalOpenObj.setModalOpen(false);
  };

  const calDiff = () => {
    return (
      cashAmount -
      ((summaryData.initial_cash || 0) -
        (summaryData.cash_float || 0) +
        (summaryData.cash_removal || 0) +
        (summaryData.system_balance?.cash || 0) -
        (summaryData.cash_out || 0) -
        (summaryData.sale_refund || 0))
    );
  };

  const handleTransactionSuccess = () => {
    setIsSuccessModalOpen(false);

    setIsTransactionPrintPopupOpen(true);
    setTimeout(() => {
      setIsTransactionPrintPopupOpen(false);
    }, 5000);
  };

  const getUserFullName = () => {
    if (!user) return '...';
    const firstName = user.first_name || '';
    const lastName = user.last_name || '';
    return `${firstName} ${lastName}`.trim();
  };

  const getPosName = () => {
    if (posData?.[0]?.name) {
      return posData[0].name;
    }
    if (posData?.data?.name) {
      return posData.data.name;
    }
    return '';
  };

  const renderHeader = () => {
    const renderInitialCashAmount = () => (
      <div className="text-20 leading-[2] font-[700] min-w-[min(22.07vw,226px)]">
        ยอดเงินตั้งต้นเดิม
        <span className="ml-[5px]">
          {currentMenu === DRAWER_OPERATION_TYPES.DEPOSIT
            ? numberWithTwoDecimals(totalCashFloat)
            : currentMenu === DRAWER_OPERATION_TYPES.WITHDRAW
            ? numberWithTwoDecimals(totalCashRemoval)
            : isLoading
            ? 'กำลังโหลด...'
            : initialCashData
            ? numberWithTwoDecimals(initialCashData.cash_total)
            : '0'}
        </span>
      </div>
    );

    if (currentMenu === DRAWER_OPERATION_TYPES.DEPOSIT || currentMenu === DRAWER_OPERATION_TYPES.WITHDRAW) {
      return (
        <div className="justify-between">
          <div className="text-20 leading-[2] font-[700] w-[min(26.86vw,275px)]">
            {currentOperation.headerText}
            <span className="ml-[10px]">
              {currentMenu === DRAWER_OPERATION_TYPES.DEPOSIT
                ? cashFloatCount + 1
                : currentMenu === DRAWER_OPERATION_TYPES.WITHDRAW
                ? cashRemovalCount + 1
                : 1}
            </span>
          </div>
          {renderInitialCashAmount()}
        </div>
      );
    }

    if (currentMenu === DRAWER_OPERATION_TYPES.CLOSE_BALANCE) {
      return (
        <div className="mb-[min(1.56vw,16px)]">
          <div className="leading-[2] text-20 font-[700]">{currentOperation.headerText}</div>
        </div>
      );
    }

    return null;
  };

  const renderCloseBalanceContent = () => {
    if (currentMenu !== DRAWER_OPERATION_TYPES.CLOSE_BALANCE) return null;

    return (
      <>
        <div className="text-20 leading-[2]">ยอดขายแบ่งตามช่องทาง</div>
        <div className="flex text-18 leading-[1.1] gap-[min(3.03vw,31px)]">
          <div className="w-[min(30.43vw,210px)] mt-2 flex-grow-0 flex flex-col gap-[min(1.76vw,18px)]">
            <div className="flex justify-between">
              <div className="text-[#606060]">เงินสด</div>
              <div className="text-[var(--color-green)]">{numberWithTwoDecimals(Number(summaryData.system_balance.cash) - Number(summaryData.sale_refund))}</div>
            </div>
            <div className="flex justify-between">
              <div className="text-[#606060]">บัตรเครดิต</div>
              <div className="text-[var(--color-green)]">
                {numberWithTwoDecimals(summaryData.system_balance.credit_card)}
              </div>
            </div>
            <div className="flex justify-between">
              <div className="text-[#606060]">ลูกหนี้</div>
              <div className="text-[var(--color-green)]">
                {numberWithTwoDecimals(summaryData.system_balance.accounts_receivable)}
              </div>
            </div>
            <div className="flex justify-between">
              <div className="text-[#606060]">คะแนนสมาชิก</div>
              <div className="text-[var(--color-green)]">{numberWithTwoDecimals(summaryData.system_balance.point)}</div>
            </div>
          </div>

          <div className="mt-2 flex-grow flex flex-col gap-[min(1.76vw,18px)]">
            <div className="flex justify-between">
              <div className="text-[#606060]">เงินโอน</div>
              <div className="text-[var(--color-green)]">
                {numberWithTwoDecimals(summaryData.system_balance.bank_transfer)}
              </div>
            </div>
            <div className="flex justify-between">
              <div className="text-[#606060]">รับชำระผ่านผู้ให้บริการ</div>
              <div className="text-[var(--color-green)]">
                {numberWithTwoDecimals(summaryData.system_balance.delivery)}
              </div>
            </div>
            <div className="flex justify-between">
              <div className="text-[#606060]">คูปอง</div>
              <div className="text-[var(--color-green)]">
                {numberWithTwoDecimals(summaryData.system_balance.coupon)}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-[#EDEDED] w-full h-[min(0.10vw,1px)] mt-[min(1.96vw,20.12px)]"></div>

        <div className="text-20 leading-[2]">ยอดนับจริง</div>
        <div className="flex text-18 leading-[1.1] gap-[min(3.03vw,31px)]">
          <div className="w-[min(30.43vw,210px)] mt-2 flex-grow-0 flex flex-col gap-[min(1.76vw,18px)]">
            <div className="flex justify-between items-center">
              <div className="text-[#606060]">ยอดนับเงินสด</div>
              <div className="flex items-center gap-[min(1.27vw,13px)]">
                <div className="w-[min(10.67vw,130px)]">
                  <CustomInput
                    inputprops={{
                      type: 'text',
                      className: 'modal-input text-18 disabled-input text-[var(--color-green)]',
                      readOnly: true,
                      value: numberWithTwoDecimals(cashAmount),
                      min: 0,
                      onClick: e => {
                        e.target.select();
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="mt-2 flex-grow flex flex-col gap-[min(0.88vw,9px)]">
            <div className="flex justify-between items-center">
              <div className="text-[#606060]">Diff</div>
              <div className="w-[min(13.67vw,140px)]">
                <CustomInput
                  inputprops={{
                    type: 'text',
                    className: `modal-input text-18 disabled-input ${
                      paymentMethodTotals.differenceAmount < 0 ? 'text-[var(--color-red)]' : 'text-[var(--color-green)]'
                    }`,
                    readOnly: true,
                    value: numberWithTwoDecimals(calDiff()),
                    min: 0,
                    onClick: e => {
                      e.target.select();
                    }
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {hasPendingBills && (
          <div className="mt-4 text-center">
            <div className="text-[var(--color-red)] text-16 font-medium mb-2">{pendingBillsErrorMessage}</div>
          </div>
        )}

        <div className="flex justify-center mt-[min(2.44vw,25px)]">
          <CustomBtn
            onClick={() => {
              if (!hasPendingBills) {
                modalOpenObj.setModalOpen(false);
                setIsPinModalOpen(true);
              }
            }}
            className={`leading-[0.8] w-fit min-w-[min(10.25vw,105px)] text-18 text-white font-[600] min-h-[min(3.91vw,40px)] rounded-[min(0.78vw,8px)] ${
              isBalanceCloseConfirmed && !hasPendingBills
                ? 'btn-error-active !bg-[#2DC76D]'
                : hasPendingBills
                ? 'disabled-btn !bg-[#A0A0A0] cursor-not-allowed'
                : 'btn-error-active !bg-[#2DC76D]'
            }`}
            btntxt={'ปิดยอด'}
          />
        </div>
      </>
    );
  };

  return (
    <div>
      <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        open={modalOpenObj.modalOpen}
        onClose={handleCloseModal}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 500
          }
        }}
        style={{ overflow: 'auto' }}
      >
        <Fade in={modalOpenObj.modalOpen}>
          <div className="bg-white w-[min(58.98vw,604px)] px-[min(2.44vw,25px)] pt-[min(2.44vw,25px) pb-[min(1.46vw,15px)] absolute top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] rounded-[min(1.46vw,15px)] text-20 text-[var(--color-black-2)]">
            <div className="relative">
              <button
                onClick={handleCloseModal}
                className="absolute top-0 right-0 aspect-square w-[min(3.90vw,39.97px)] rounded-full bg-[#F5F5F5] flex justify-center items-center"
              >
                <img
                  className="aspect-square object-contain w-[min(0.98vw,10px)]"
                  src="/img/icons/icon-close.svg"
                  alt="Close"
                />
              </button>
            </div>

            {renderHeader()}

            <div>
              <CashDenominationCounter
                startValueObj={{
                  startValue: cashAmount,
                  setStartValue: setCashAmount
                }}
                info={{
                  ...cashierOperationInfo,
                  initialAmount: initialCashData ? initialCashData.cash_total : cashierOperationInfo.initialAmount
                }}
                viaPaymentInfoObj={{
                  viaPaymentInfo: paymentMethodTotals,
                  setViaPaymentInfo: setPaymentMethodTotals
                }}
                isResetObj={{
                  isReset: shouldResetMoneyController,
                  setIsReset: setShouldResetMoneyController
                }}
                isCloseBalance={currentMenu === DRAWER_OPERATION_TYPES.CLOSE_BALANCE}
                isCloseBalanceCfObg={{
                  isCloseBalanceConfirm: isBalanceCloseConfirmed,
                  setIsCloseBalanceConfirm: setIsBalanceCloseConfirmed
                }}
                cashData={summaryData}
                onCashBreakdownChange={updateCashBreakdown}
              />

              <div className="bg-[#EDEDED] w-full h-[min(0.10vw,1px)] mt-[min(1.96vw,20.12px)]"></div>

              {currentMenu !== DRAWER_OPERATION_TYPES.CLOSE_BALANCE && (
                <div className="flex justify-center">
                  <CustomBtn
                    onClick={handleSubmit}
                    className={`leading-[0.8] min-w-[min(24.71vw,253px)] text-white font-[400] text-16 min-h-[min(3.91vw,40px)] rounded-[min(0.78vw,8px)] mt-[min(1.56vw,16px)] mb-[min(0.98vw,10px)] ${
                      isSubmitButtonActive ? 'btn-main-active' : 'disabled-btn'
                    }`}
                    btntxt={currentOperation.buttonText}
                  />
                </div>
              )}

              {renderCloseBalanceContent()}
            </div>
          </div>
        </Fade>
      </Modal>

      <PinVerificationModal
        modalOpenObj={{
          modalOpen: isPinModalOpen,
          setModalOpen: setIsPinModalOpen
        }}
        modalSuccessOpenObj={{
          modalOpen: currentMenu !== DRAWER_OPERATION_TYPES.CLOSE_BALANCE ? isSuccessModalOpen : false,
          setModalOpen: currentMenu !== DRAWER_OPERATION_TYPES.CLOSE_BALANCE ? setIsSuccessModalOpen : () => {}
        }}
        onPinVerified={handlePinVerificationSuccess}
      />

      <TransactionSuccessModal
        modalOpenObj={{
          modalOpen: isSuccessModalOpen,
          setModalOpen: setIsSuccessModalOpen
        }}
        startValueObj={{
          startValue: cashAmount,
          setStartValue: setCashAmount
        }}
        isResetMoneyControllerObj={{
          isReset: shouldResetMoneyController,
          setReset: setShouldResetMoneyController
        }}
        info={{
          headModalTxt: currentOperation.successModalHeader
        }}
        onClose={handleTransactionSuccess}
      />

      <CloseBalanceConfirmationModal
        info={{
          diffTotal: calDiff()
        }}
        modalOpenObj={{
          modalOpen: isCloseBalanceConfirmModalOpen,
          setModalOpen: setIsCloseBalanceConfirmModalOpen
        }}
        onConfirm={handleConfirmCloseBalance}
      />

      <ModalPOSCashTransactionPrint
        isOpen={isTransactionPrintPopupOpen}
        cashBreakdown={backupCashBreakdown}
        printType={currentOperation.type}
        businessName={businessInfo.businessName}
        branchName={businessInfo.branchName}
        posInfo={pos}
        vatCalculator={businessInfo.vatCalculator}
        taxNumber={businessInfo.taxNumber}
        posName={getPosName()}
        userFullName={getUserFullName()}
        transactionNumber={
          currentMenu === DRAWER_OPERATION_TYPES.DEPOSIT
            ? cashFloatCount + 1
            : currentMenu === DRAWER_OPERATION_TYPES.WITHDRAW
            ? cashRemovalCount + 1
            : 1
        }
        totalCashFloat={totalCashFloat + 1}
        totalCashRemoval={totalCashRemoval + 1}
      />
    </div>
  );
}
